import {
  stateAssinatura,
  gettersAssinatura,
  mutationsAssinatura,
  actionsAssinatura,
} from '@modules/ai-framework/store/creditos-assinatura'
import {
  stateTermos,
  gettersTermos,
  mutationsTermos,
  actionsTermos,
} from '@modules/ai-framework/store/creditos-termos'

// Slice `creditos` (project-ia-payment) — sub-módulo namespaced `aiFramework/creditos/*`.
// Carteira do PROFISSIONAL (ADR-008); o tenant continua sendo o escritório, garantido
// server-side.
//
// ★ QUEM BUSCA É A API. Toda action daqui é setter puro (commita, e só): quem faz o request,
// versiona as leituras, guarda o duplo clique e aplica o desfecho é `apiAiFrameworkCreditos`
// (saldo, pacotes, checkout, pagamento, extrato, resumo) e `apiAiFrameworkAssinatura`
// (planos, assinatura) — molde de `_templates/new/api/api.ejs.t`. Regra que atravessa tudo:
// **o cliente nunca soma crédito** — depois de `creditado: true` o saldo é RELIDO do servidor.
//
// Mesmo tamanho de página do `PAGE_LIMIT` da tela de execuções e default do contrato.
const EXTRATO_LIMIT_PADRAO = 20

const state = () => ({
  // O plano recorrente (spec-005) vive em `creditos-assinatura.js` — mesmo slice
  // Vuex, arquivo próprio. Ele fala de um assunto fechado (catálogo de planos,
  // assinatura, ciclo) e mantê-lo aqui empurrava este arquivo para 760 linhas.
  ...stateAssinatura(),

  // O aceite dos Termos de Uso da contratação (spec-009 [US-1]) vive em
  // `creditos-termos.js` — mesmo slice, arquivo próprio: quem o lê é a tela de planos e
  // quem o grava é o mesmo clique que cria a assinatura.
  ...stateTermos(),

  // Payload do contrato ({id_profissional, creditos, equivalente_brl, negativo,
  // atualizado_em}) ou null. `creditos` fica como STRING de 6 casas — o store NÃO converte
  // para número: quem arredonda é a exibição (ADR-007).
  saldo: null,
  loading: false,
  forbidden: false,

  // Catálogo vigente: { pacotes: [...], valor_personalizado: {...} } ou null.
  // `pacotes: []` é catálogo VAZIO (estado de tela), diferente de `null` (não carregado).
  pacotes: null,
  pacotesLoading: false,
  // Mensagem do backend quando a leitura falha — a tela mostra erro com "tentar de novo"
  // (US-1), e NUNCA cai para um catálogo hardcoded.
  pacotesErro: null,

  // Sessão de checkout criada com sucesso ({id_pagamento, url_checkout, ...}) ou null.
  checkout: null,
  checkoutLoading: false,
  // { message, idPagamento } quando a criação falha. O `idPagamento` só existe no 502
  // (Stripe indisponível): ali o pagamento EXISTE em `falhou` e o id é o protocolo que o
  // suporte usa para investigar "paguei e não caiu" (contracts/api.md).
  checkoutErro: null,

  // Estado de UM pagamento, lido em polling pela tela de retorno.
  pagamento: null,
  pagamentoLoading: false,
  pagamentoNaoEncontrado: false,
  // Id do pagamento cujo crédito já disparou a releitura do saldo. Sem isto, cada poll
  // sobre um pagamento já creditado dispararia um `carregarSaldo` novo — uma requisição por
  // intervalo sobre um número que parou de mudar.
  saldoRecarregadoPara: null,

  // ── spec-003 ([T011]): dois blocos de tela, dois estados independentes ──
  // Lançamentos da página corrente. `[]` é "período sem movimento" (estado de tela) e só
  // pode ser lido assim quando `extratoErro` e `extratoForbidden` estão limpos — é a
  // distinção que impede a lista vazia de uma falha se passar por "não houve consumo".
  extrato: [],
  extratoTotal: 0,
  // Recorte APLICADO (o que está na tela): a API grava filtro e página ANTES do request e
  // lê daqui o que manda — é o que faz o "tentar de novo" repetir a última consulta.
  extratoFilters: { de: null, ate: null, natureza: null, idProfissional: null },
  extratoPage: { limit: EXTRATO_LIMIT_PADRAO, offset: 0 },
  extratoLoading: false,
  extratoForbidden: false,
  extratoErro: null,
  // spec-006 [US-4]: a fronteira entre as réguas de cobrança do período. Vive
  // JUNTO do extrato (mesmo ciclo de leitura) porque descreve exatamente as
  // linhas listadas — separá-lo permitiria a lista e o aviso divergirem por
  // uma resposta obsoleta. `mudou: false` + `reguas: []` é o estado honesto
  // enquanto não houve leitura confirmada: sem aviso, e sem afirmar que não há
  // fronteira.
  extratoRegua: { mudou: false, reguas: [], marco: null },

  // Agregado do período ({creditos_consumidos, total_pago_brl, compra_disponivel, ...})
  // ou `null`. ★ `null` é ausência de dado e a tela mostra o estado de falha — NUNCA
  // zeros: `0,00` num card de consumo se lê como "não gastei nada", que é a mesma mentira
  // que `0,00` conta na coluna de um run não debitado.
  resumo: null,
  resumoFilters: { de: null, ate: null, idProfissional: null },
  resumoLoading: false,
  resumoForbidden: false,
  resumoErro: null,
})

const getters = {
  ...gettersAssinatura,
  ...gettersTermos,

  saldoCreditos: (s) => s.saldo,
  saldoLoading: (s) => s.loading,
  saldoForbidden: (s) => s.forbidden,

  pacotes: (s) => s.pacotes,
  pacotesLoading: (s) => s.pacotesLoading,
  pacotesErro: (s) => s.pacotesErro,

  checkout: (s) => s.checkout,
  checkoutLoading: (s) => s.checkoutLoading,
  checkoutErro: (s) => s.checkoutErro,

  pagamento: (s) => s.pagamento,
  pagamentoLoading: (s) => s.pagamentoLoading,
  pagamentoNaoEncontrado: (s) => s.pagamentoNaoEncontrado,
  saldoRecarregadoPara: (s) => s.saldoRecarregadoPara,

  extrato: (s) => s.extrato || [],
  extratoTotal: (s) => s.extratoTotal || 0,
  extratoFilters: (s) => s.extratoFilters || {},
  extratoPage: (s) => s.extratoPage || {},
  extratoLoading: (s) => s.extratoLoading,
  extratoForbidden: (s) => s.extratoForbidden,
  extratoErro: (s) => s.extratoErro,
  extratoReguaMudou: (s) => (s.extratoRegua || {}).mudou === true,
  extratoReguas: (s) => (s.extratoRegua || {}).reguas || [],
  extratoMarcoRegua: (s) => (s.extratoRegua || {}).marco || null,

  resumo: (s) => s.resumo,
  resumoFilters: (s) => s.resumoFilters || {},
  resumoLoading: (s) => s.resumoLoading,
  resumoForbidden: (s) => s.resumoForbidden,
  resumoErro: (s) => s.resumoErro,
}

const mutations = {
  ...mutationsAssinatura,
  ...mutationsTermos,

  SET_SALDO(s, payload) {
    s.saldo = payload || null
  },
  SET_LOADING(s, payload) {
    s.loading = payload
  },
  SET_FORBIDDEN(s, payload) {
    s.forbidden = payload
  },

  SET_PACOTES(s, payload) {
    s.pacotes = payload || null
  },
  SET_PACOTES_LOADING(s, payload) {
    s.pacotesLoading = payload
  },
  SET_PACOTES_ERRO(s, payload) {
    s.pacotesErro = payload || null
  },

  SET_CHECKOUT(s, payload) {
    s.checkout = payload || null
  },
  SET_CHECKOUT_LOADING(s, payload) {
    s.checkoutLoading = payload
  },
  SET_CHECKOUT_ERRO(s, payload) {
    s.checkoutErro = payload || null
  },

  SET_PAGAMENTO(s, payload) {
    s.pagamento = payload || null
  },
  SET_PAGAMENTO_LOADING(s, payload) {
    s.pagamentoLoading = payload
  },
  SET_PAGAMENTO_NAO_ENCONTRADO(s, payload) {
    s.pagamentoNaoEncontrado = payload
  },
  SET_SALDO_RECARREGADO_PARA(s, payload) {
    s.saldoRecarregadoPara = payload || null
  },

  SET_EXTRATO(s, payload) {
    s.extrato = payload || []
  },
  SET_EXTRATO_TOTAL(s, payload) {
    s.extratoTotal = payload || 0
  },
  // Merge (e não substituição): paginar manda só `{ offset }` e não pode perder o
  // recorte já aplicado — mesmo desenho do `SET_FILTERS`/`SET_PAGE` do slice `runs`.
  SET_EXTRATO_FILTERS(s, payload) {
    s.extratoFilters = { ...s.extratoFilters, ...payload }
  },
  SET_EXTRATO_PAGE(s, payload) {
    s.extratoPage = { ...s.extratoPage, ...payload }
  },
  SET_EXTRATO_LOADING(s, payload) {
    s.extratoLoading = payload
  },
  SET_EXTRATO_FORBIDDEN(s, payload) {
    s.extratoForbidden = payload
  },
  SET_EXTRATO_ERRO(s, payload) {
    s.extratoErro = payload || null
  },
  // Substituição, não merge: a fronteira é do recorte ATUAL. Um merge deixaria a
  // régua de um período anterior sobrevivendo à troca de filtro, e o aviso
  // passaria a descrever linhas que não estão mais na tela.
  SET_EXTRATO_REGUA(s, payload) {
    const dados = payload || {}
    s.extratoRegua = {
      mudou: dados.mudou === true,
      reguas: dados.reguas || [],
      marco: dados.marco || null,
    }
  },

  SET_RESUMO(s, payload) {
    s.resumo = payload || null
  },
  SET_RESUMO_FILTERS(s, payload) {
    s.resumoFilters = { ...s.resumoFilters, ...payload }
  },
  SET_RESUMO_LOADING(s, payload) {
    s.resumoLoading = payload
  },
  SET_RESUMO_FORBIDDEN(s, payload) {
    s.resumoForbidden = payload
  },
  SET_RESUMO_ERRO(s, payload) {
    s.resumoErro = payload || null
  },
}

// Setters: só commitam, um por mutation. Quem os chama é a `apiAiFrameworkCreditos`,
// depois de ler ou escrever no servidor.
const actions = {
  ...actionsAssinatura,
  ...actionsTermos,

  setSaldo({ commit }, payload) {
    commit('SET_SALDO', payload)
  },
  setSaldoLoading({ commit }, payload) {
    commit('SET_LOADING', payload)
  },
  setSaldoForbidden({ commit }, payload) {
    commit('SET_FORBIDDEN', payload)
  },
  setPacotes({ commit }, payload) {
    commit('SET_PACOTES', payload)
  },
  setPacotesLoading({ commit }, payload) {
    commit('SET_PACOTES_LOADING', payload)
  },
  setPacotesErro({ commit }, payload) {
    commit('SET_PACOTES_ERRO', payload)
  },
  setCheckout({ commit }, payload) {
    commit('SET_CHECKOUT', payload)
  },
  setCheckoutLoading({ commit }, payload) {
    commit('SET_CHECKOUT_LOADING', payload)
  },
  setCheckoutErro({ commit }, payload) {
    commit('SET_CHECKOUT_ERRO', payload)
  },
  setPagamento({ commit }, payload) {
    commit('SET_PAGAMENTO', payload)
  },
  setPagamentoLoading({ commit }, payload) {
    commit('SET_PAGAMENTO_LOADING', payload)
  },
  setPagamentoNaoEncontrado({ commit }, payload) {
    commit('SET_PAGAMENTO_NAO_ENCONTRADO', payload)
  },
  setSaldoRecarregadoPara({ commit }, payload) {
    commit('SET_SALDO_RECARREGADO_PARA', payload)
  },
  setExtrato({ commit }, payload) {
    commit('SET_EXTRATO', payload)
  },
  setExtratoTotal({ commit }, payload) {
    commit('SET_EXTRATO_TOTAL', payload)
  },
  setExtratoFilters({ commit }, payload) {
    commit('SET_EXTRATO_FILTERS', payload)
  },
  setExtratoPage({ commit }, payload) {
    commit('SET_EXTRATO_PAGE', payload)
  },
  setExtratoLoading({ commit }, payload) {
    commit('SET_EXTRATO_LOADING', payload)
  },
  setExtratoForbidden({ commit }, payload) {
    commit('SET_EXTRATO_FORBIDDEN', payload)
  },
  setExtratoErro({ commit }, payload) {
    commit('SET_EXTRATO_ERRO', payload)
  },
  setExtratoRegua({ commit }, payload) {
    commit('SET_EXTRATO_REGUA', payload)
  },
  setResumo({ commit }, payload) {
    commit('SET_RESUMO', payload)
  },
  setResumoFilters({ commit }, payload) {
    commit('SET_RESUMO_FILTERS', payload)
  },
  setResumoLoading({ commit }, payload) {
    commit('SET_RESUMO_LOADING', payload)
  },
  setResumoForbidden({ commit }, payload) {
    commit('SET_RESUMO_FORBIDDEN', payload)
  },
  setResumoErro({ commit }, payload) {
    commit('SET_RESUMO_ERRO', payload)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
